import { useCallback, useMemo } from 'react';
import { useGetV4ManufacturerByRouteManufacturerIdSearchQuery } from 'randmar-api-client';
import SearchResult from '../../stores/search/SearchResult';

/**
 * Custom hook for handling the manufacturer search logic.
 * It fetches, transforms, and returns the search results.
 * @param query The search string.
 * @param applicationId The current manufacturer's application ID.
 * @returns The search results and loading state.
 */

export const useManufacturerSearch = (query: string, applicationId: string) => {
  const normalizedQuery = query.trim();
  const hasQuery = normalizedQuery.length > 0;

  const {
    data: searchResults,
    currentData,
    isLoading,
    isFetching,
    refetch,
    fulfilledTimeStamp,
  } = useGetV4ManufacturerByRouteManufacturerIdSearchQuery(
    {
      routeManufacturerId: applicationId,
      s: normalizedQuery,
    },
    {
      skip: !hasQuery,
      refetchOnMountOrArgChange: true,
    }
  );

  const sourceResults = currentData ?? searchResults;

  const convertedResults = useMemo(() => {
    if (!Array.isArray(sourceResults)) return [];
    return sourceResults.filter((r) => r?.Content).map((r) => SearchResult.convertFrom(r, r.Content));
  }, [sourceResults]);

  const isInitialLoading = hasQuery && isLoading && !Array.isArray(sourceResults);
  const isBackgroundFetching = hasQuery && isFetching && !isInitialLoading;

  const reload = useCallback(async () => {
    if (!hasQuery) return;
    await refetch();
  }, [hasQuery, refetch]);

  return {
    results: convertedResults,
    isInitialLoading,
    isBackgroundFetching,
    reload,
    lastRefreshedAt: fulfilledTimeStamp ? new Date(fulfilledTimeStamp) : null,
  };
};
